import React, { useState } from 'react';
import Button from '@components/common/Button';
import { SearchableSelect } from '@components/common/SearchableSelect';

export type CASelfDeclarationEntityType =
  | 'individual'
  | 'sole_proprietor'
  | 'corporation'
  | 'partnership'
  | 'trust';

export interface CASelfDeclarationValues {
  entity_type: CASelfDeclarationEntityType;
  legal_name: string;
  business_number?: string;
  sin?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  province: string;
  postal_code: string;
  provides_construction_services: boolean;
  signature_full_name: string;
}

interface Props {
  onSubmit: (values: CASelfDeclarationValues) => Promise<void> | void;
  isPending?: boolean;
}

const PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU',
  'ON', 'PE', 'QC', 'SK', 'YT',
] as const;
const CA_ENTITY_TYPE_OPTIONS = [
  { id: 'individual', label: 'Individual' },
  { id: 'sole_proprietor', label: 'Sole Proprietor' },
  { id: 'corporation', label: 'Corporation' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'trust', label: 'Trust' },
];
const PROVINCE_OPTIONS = PROVINCES.map((province) => ({ id: province, label: province }));

const BN_PATTERN = /^\d{9}[A-Z]{2}\d{4}$/;
const SIN_PATTERN = /^[0-9]{3}-?[0-9]{3}-?[0-9]{3}$/;

const inputClass =
  'mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

export function CASelfDeclarationForm({ onSubmit, isPending = false }: Props) {
  const [v, setV] = useState<CASelfDeclarationValues>({
    entity_type: 'corporation',
    legal_name: '',
    business_number: '',
    sin: '',
    address_line1: '',
    address_line2: '',
    city: '',
    province: 'ON',
    postal_code: '',
    provides_construction_services: false,
    signature_full_name: '',
  });
  const [error, setError] = useState<string | null>(null);

  const isBusiness = v.entity_type !== 'individual';
  const requiresBN = isBusiness;
  const requiresSIN = !isBusiness;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (requiresBN) {
      const bn = (v.business_number ?? '').trim();
      if (!bn) {
        setError('Business Number is required.');
        return;
      }
      if (!BN_PATTERN.test(bn)) {
        setError('Business Number must be 9 digits, 2 letters, 4 digits (e.g., 123456789RP0001).');
        return;
      }
    }
    if (requiresSIN) {
      const sin = (v.sin ?? '').trim();
      if (!sin) {
        setError('SIN is required.');
        return;
      }
      if (!SIN_PATTERN.test(sin)) {
        setError('SIN must be 9 digits, optionally formatted as 123-456-789.');
        return;
      }
    }

    const payload: CASelfDeclarationValues = {
      ...v,
      legal_name: v.legal_name.trim(),
      business_number: requiresBN ? (v.business_number ?? '').trim() : undefined,
      sin: requiresSIN ? (v.sin ?? '').trim() : undefined,
      address_line1: v.address_line1.trim(),
      address_line2: (v.address_line2 ?? '').trim() || undefined,
      city: v.city.trim(),
      province: v.province.toUpperCase(),
      postal_code: v.postal_code.trim(),
      signature_full_name: v.signature_full_name.trim(),
    };
    await onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 ca-self-declaration-form">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Canadian Tax Information
        </h2>
        <p className="mt-1 text-sm text-content-secondary dark:text-content-secondary-dark">
          Declare your Canadian residency, tax identifier, and (when applicable) whether you
          provide construction services. Linked customers use this to determine T4A or T5018
          reporting.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className={labelClass}>
          Entity type
          <SearchableSelect
            value={v.entity_type}
            onChange={(value) =>
              setV({ ...v, entity_type: value as CASelfDeclarationEntityType })
            }
            options={CA_ENTITY_TYPE_OPTIONS}
            placeholder="Entity type"
            clearable={false}
            className="mt-1"
          />
        </label>

        <label className={labelClass}>
          Legal name<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.legal_name}
            onChange={(e) => setV({ ...v, legal_name: e.target.value })}
            className={inputClass}
          />
        </label>

        {requiresBN && (
          <label className={labelClass}>
            Business Number<span className="text-red-500">*</span>
            <input
              type="text"
              required
              placeholder="123456789RP0001"
              pattern="[0-9]{9}[A-Z]{2}[0-9]{4}"
              value={v.business_number ?? ''}
              onChange={(e) => setV({ ...v, business_number: e.target.value.toUpperCase() })}
              className={inputClass}
            />
          </label>
        )}

        {requiresSIN && (
          <label className={labelClass}>
            SIN<span className="text-red-500">*</span>
            <input
              type="text"
              required
              placeholder="123-456-789"
              pattern="[0-9]{3}-?[0-9]{3}-?[0-9]{3}"
              value={v.sin ?? ''}
              onChange={(e) => setV({ ...v, sin: e.target.value })}
              className={inputClass}
            />
          </label>
        )}

        <label className={`${labelClass} md:col-span-2`}>
          Address line 1<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.address_line1}
            onChange={(e) => setV({ ...v, address_line1: e.target.value })}
            className={inputClass}
          />
        </label>

        <label className={`${labelClass} md:col-span-2`}>
          Address line 2
          <input
            type="text"
            value={v.address_line2 ?? ''}
            onChange={(e) => setV({ ...v, address_line2: e.target.value })}
            className={inputClass}
          />
        </label>

        <label className={labelClass}>
          City<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.city}
            onChange={(e) => setV({ ...v, city: e.target.value })}
            className={inputClass}
          />
        </label>

        <label className={labelClass}>
          Province<span className="text-red-500">*</span>
          <SearchableSelect
            value={v.province}
            onChange={(value) => setV({ ...v, province: value })}
            options={PROVINCE_OPTIONS}
            placeholder="Province"
            clearable={false}
            className="mt-1"
          />
        </label>

        <label className={labelClass}>
          Postal code<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.postal_code}
            onChange={(e) => setV({ ...v, postal_code: e.target.value.toUpperCase() })}
            className={inputClass}
          />
        </label>

        <label className="md:col-span-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={v.provides_construction_services}
            onChange={(e) =>
              setV({ ...v, provides_construction_services: e.target.checked })
            }
          />
          I provide construction services (drives T5018 reporting)
        </label>

        <label className={`${labelClass} md:col-span-2`}>
          Signature (full name)<span className="text-red-500">*</span>
          <input
            type="text"
            required
            value={v.signature_full_name}
            onChange={(e) => setV({ ...v, signature_full_name: e.target.value })}
            className={inputClass}
          />
        </label>
      </div>

      <div className="flex justify-end border-t border-gray-200 pt-4">
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Submitting...' : 'Certify and Submit'}
        </Button>
      </div>
    </form>
  );
}

export default CASelfDeclarationForm;
